import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";

import IndustrialProduct from "./components/IndustrialProduct";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import WhatsAppButton from "./components/WhatsAppButton";

import "./IndustrialR.css";

/* ============================================================
   API
============================================================ */

const API_URL =
  process.env.NODE_ENV === "production"
    ? "https://gj-frio-industrial-api.onrender.com"
    : "http://localhost:4000";

/* ============================================================
   OBTENER CATÁLOGO INDUSTRIAL
============================================================ */

const obtenerCatalogoIndustrial = async () => {
  const respuesta = await axios.get(API_URL, {
    timeout: 45000,
  });

  return respuesta.data || {};
};

/* ============================================================
   COMPONENTE
============================================================ */

function IndustrialR() {
  /* ============================================================
     FILTROS
  ============================================================ */

  const [filtroSeleccionado, setFiltroSeleccionado] = useState("todos");

  const [filtroBusqueda, setFiltroBusqueda] = useState("");

  const [paginaActual, setPaginaActual] = useState(1);

  const productosPorPagina = 16;

  /* ============================================================
     BÚSQUEDA RECIBIDA DESDE NAVBAR
  ============================================================ */

  const location = useLocation();

  const params = new URLSearchParams(location.search);

  const busquedaDesdeNavbar =
    params.get("search") || params.get("busqueda") || "";

  /* ============================================================
     CATÁLOGO
     TANSTACK QUERY + INDEXEDDB

     La primera vez obtiene los productos desde la API.
     Después TanStack Query puede reutilizar la información
     almacenada en la caché persistente de IndexedDB.
  ============================================================ */

  const {
    data: catalogo = {},
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["catalogo-industrial"],
    queryFn: obtenerCatalogoIndustrial,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  if (isError) {
    console.error("Error al obtener productos del catálogo industrial:", error);
  }

  /* ============================================================
     BÚSQUEDA DESDE NAVBAR
  ============================================================ */

  useEffect(() => {
    if (busquedaDesdeNavbar) {
      setFiltroBusqueda(busquedaDesdeNavbar);
      setFiltroSeleccionado("todos");
      setPaginaActual(1);
    }
  }, [busquedaDesdeNavbar]);

  /* ============================================================
     SCROLL AL CAMBIAR PÁGINA
  ============================================================ */

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [paginaActual]);

  /* ============================================================
     NORMALIZAR TEXTO
  ============================================================ */

  const normalizarTexto = (texto = "") => {
    return String(texto)
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();
  };

  /* ============================================================
     COMPROBAR BÚSQUEDA
  ============================================================ */

  const coincideBusqueda = (item) => {
    if (!filtroBusqueda.trim()) {
      return true;
    }

    const palabras = normalizarTexto(filtroBusqueda)
      .split(/\s+/)
      .filter(Boolean);

    const textoProducto = normalizarTexto(
      `${item?.nombre || ""} ${item?.marca || ""} ${
        item?.modelo || ""
      } ${item?.codigoProducto || ""} ${item?.descripcion || ""}`,
    );

    return palabras.every((palabra) => textoProducto.includes(palabra));
  };

  /* ============================================================
     CATEGORÍAS

     Ya no necesitamos 15 useState diferentes.
     Todos los productos salen directamente del objeto almacenado
     por TanStack Query.
  ============================================================ */

  const categorias = useMemo(
    () => ({
      axial: catalogo.axiales || [],
      aceite: catalogo.aceites || [],
      cilindro: catalogo.cilindros || [],
      separador: catalogo.separadores || [],
      portaFiltro: catalogo.portaFiltros || [],
      ducto: catalogo.ductos || [],
      controlador: catalogo.controladores || [],
      eliminador: catalogo.eliminadores || [],
      valvula: catalogo.valvulas || [],
      orificio: catalogo.orificios || [],
      bobina: catalogo.bobinas || [],
      selenoide: catalogo.selenoides || [],
      presostato: catalogo.presostatos || [],
      piedra: catalogo.piedras || [],
      filtro: catalogo.filtros || [],
    }),
    [catalogo],
  );

  /* ============================================================
     TODOS LOS PRODUCTOS
  ============================================================ */

  const todosLosProductos = useMemo(() => {
    return Object.values(categorias).flat();
  }, [categorias]);

  /* ============================================================
     PRODUCTOS FILTRADOS
  ============================================================ */

  const productosFiltrados = useMemo(() => {
    const resultado = {};

    Object.entries(categorias).forEach(([categoria, productos]) => {
      resultado[categoria] = productos.filter(coincideBusqueda);
    });

    resultado.todos = todosLosProductos.filter(coincideBusqueda);

    return resultado;
  }, [categorias, todosLosProductos, filtroBusqueda]);

  /* ============================================================
     NOMBRES LEGIBLES
  ============================================================ */

  const nombresLegibles = {
    todos: "Todos los productos",
    axial: "Motores Axiales",
    aceite: "Aceites",
    cilindro: "Cilindros Refrigerantes",
    separador: "Separadores de Aceite",
    portaFiltro: "Porta Filtros",
    ducto: "Ductos",
    controlador: "Controladores Digitales",
    eliminador: "Eliminadores de Vibración",
    valvula: "Válvulas de Expansión",
    orificio: "Orificios para Válvulas",
    bobina: "Bobinas Selenoides",
    selenoide: "Válvulas Selenoide",
    presostato: "Presostatos de Aceite",
    piedra: "Filtros Elemento Piedra",
    filtro: "Filtros Roscables y Soldables",
  };

  /* ============================================================
     TÍTULOS DE CATEGORÍA
  ============================================================ */

  const titulosCategoria = {
    todos: "Componentes para Refrigeración Industrial",
    axial: "Motores Axiales",
    aceite: "Aceites para Compresores de Refrigeración",
    cilindro: "Cilindros Refrigerantes",
    separador: "Separadores de Aceite",
    portaFiltro: "Porta Filtros",
    ducto: "Ductos de Climatización",
    controlador: "Controladores de Temperatura",
    eliminador: "Eliminadores de Vibración",
    valvula: "Válvulas de Expansión",
    orificio: "Orificios para Válvulas de Expansión",
    bobina: "Bobinas para Selenoides",
    selenoide: "Válvulas Selenoides",
    presostato: "Presostatos de Aceite",
    piedra: "Filtros Elemento de Piedra",
    filtro: "Filtros Roscables y Soldables",
  };

  const obtenerTituloCategoria = () =>
    titulosCategoria[filtroSeleccionado] || titulosCategoria.todos;

  /* ============================================================
     RESULTADOS
  ============================================================ */

  const productosCategoria = productosFiltrados[filtroSeleccionado] || [];

  const totalProductos = todosLosProductos.length;

  const productosMostrados = productosCategoria.length;

  /* ============================================================
     PAGINACIÓN

     Solamente paginamos "Todos los productos".
     Las categorías individuales se mantienen completas.
  ============================================================ */

  const productosEnPagina =
    filtroSeleccionado === "todos"
      ? productosCategoria.slice(
          (paginaActual - 1) * productosPorPagina,
          paginaActual * productosPorPagina,
        )
      : productosCategoria;

  const totalPaginas =
    filtroSeleccionado === "todos"
      ? Math.ceil(productosCategoria.length / productosPorPagina)
      : 1;

  /* ============================================================
     CAMBIAR CATEGORÍA
  ============================================================ */

  const seleccionarCategoria = (categoria) => {
    setFiltroSeleccionado(categoria);
    setFiltroBusqueda("");
    setPaginaActual(1);
  };

  /* ============================================================
     CAMBIAR PÁGINA
  ============================================================ */

  const cambiarPagina = (pagina) => {
    if (pagina < 1 || pagina > totalPaginas) {
      return;
    }

    setPaginaActual(pagina);
  };

  /* ============================================================
     VISTA
  ============================================================ */

  return (
    <div className="industrial-page">
      <Navbar />

      <main>
        {/* =====================================================
            HERO / ENCABEZADO
        ===================================================== */}

        <section className="industrial-hero">
          <div className="industrial-container">
            <div className="industrial-hero-card">
              <div>
                <span className="industrial-kicker">CATÁLOGO G&amp;J FRÍO</span>

                <h1>{obtenerTituloCategoria()}</h1>

                <p>
                  Encuentra repuestos y componentes para refrigeración
                  industrial, climatización y sistemas de frío.
                </p>
              </div>

              <div className="industrial-hero-icon">
                <i className="bi bi-snow2"></i>
              </div>
            </div>
          </div>
        </section>

        {/* =====================================================
            FILTROS
        ===================================================== */}

        <section className="industrial-toolbar-section">
          <div className="industrial-container">
            <div className="industrial-toolbar">
              <div className="industrial-toolbar-left">
                <button
                  className="industrial-category-button"
                  type="button"
                  data-bs-toggle="offcanvas"
                  data-bs-target="#industrialCategories"
                  aria-controls="industrialCategories"
                >
                  <i className="bi bi-grid-fill"></i>

                  <span>Categorías</span>

                  <i className="bi bi-chevron-right"></i>
                </button>

                <div className="industrial-current-category">
                  <span>Categoría actual</span>

                  <strong>{nombresLegibles[filtroSeleccionado]}</strong>
                </div>
              </div>

              <div className="industrial-counter">
                <span>Mostrando</span>

                <strong>{productosMostrados}</strong>

                <span>de</span>

                <strong>{totalProductos}</strong>

                <span>{totalProductos === 1 ? "artículo" : "artículos"}</span>
              </div>
            </div>

            {/* =================================================
                BÚSQUEDA
            ================================================= */}

            <div className="industrial-search-row">
              <div className="industrial-search">
                <i className="bi bi-search"></i>

                <input
                  type="text"
                  placeholder="Buscar por nombre, marca, modelo o código..."
                  value={filtroBusqueda}
                  onChange={(e) => {
                    setFiltroBusqueda(e.target.value);
                    setPaginaActual(1);
                  }}
                />

                {filtroBusqueda && (
                  <button
                    type="button"
                    className="industrial-search-clear"
                    onClick={() => {
                      setFiltroBusqueda("");
                      setPaginaActual(1);
                    }}
                    aria-label="Limpiar búsqueda"
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* =====================================================
            OFFCANVAS CATEGORÍAS
        ===================================================== */}

        <div
          className="offcanvas offcanvas-start industrial-offcanvas"
          data-bs-scroll="true"
          tabIndex="-1"
          id="industrialCategories"
          aria-labelledby="industrialCategoriesLabel"
        >
          <div className="offcanvas-header industrial-offcanvas-header">
            <div>
              <span>CATÁLOGO</span>

              <h5 className="offcanvas-title" id="industrialCategoriesLabel">
                Subcategorías
              </h5>
            </div>

            <button
              type="button"
              className="btn-close btn-close-white"
              data-bs-dismiss="offcanvas"
              aria-label="Cerrar"
            ></button>
          </div>

          <div className="offcanvas-body industrial-offcanvas-body">
            <div className="industrial-category-list">
              {Object.keys(nombresLegibles).map((categoria) => {
                const cantidad = productosFiltrados[categoria]?.length || 0;

                return (
                  <button
                    type="button"
                    key={categoria}
                    className={`industrial-category-item ${
                      filtroSeleccionado === categoria ? "active" : ""
                    }`}
                    onClick={() => seleccionarCategoria(categoria)}
                    data-bs-dismiss="offcanvas"
                  >
                    <span className="industrial-category-item-icon">
                      <i
                        className={
                          categoria === "todos"
                            ? "bi bi-grid"
                            : "bi bi-box-seam"
                        }
                      ></i>
                    </span>

                    <span className="industrial-category-item-text">
                      <strong>{nombresLegibles[categoria]}</strong>

                      <small>
                        {cantidad} {cantidad === 1 ? "producto" : "productos"}
                      </small>
                    </span>

                    <i className="bi bi-chevron-right"></i>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* =====================================================
            PRODUCTOS
        ===================================================== */}

        <section className="industrial-products-section">
          <div className="industrial-container">
            {/* =================================================
                CARGANDO

                Si existe información restaurada desde IndexedDB,
                TanStack Query podrá reutilizarla.

                Este spinner queda para una primera carga donde
                todavía no existe información almacenada.
            ================================================= */}

            {isLoading ? (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{
                  minHeight: "100px",
                }}
              >
                <div
                  className="spinner-grow text-primary"
                  role="status"
                  style={{
                    width: "1.6rem",
                    height: "1.6rem",
                  }}
                >
                  <span className="visually-hidden">Cargando productos...</span>
                </div>
              </div>
            ) : productosEnPagina.length > 0 ? (
              <div className="industrial-products-grid">
                {productosEnPagina.map((item) => (
                  <IndustrialProduct
                    key={item._id}
                    img={item.imagen}
                    title={item.nombre}
                    id={item._id}
                    details={[
                      {
                        label: "Marca",
                        value: item.marca,
                      },

                      ...(item.modelo
                        ? [
                            {
                              label: "Modelo",
                              value: item.modelo,
                            },
                          ]
                        : []),

                      {
                        label: "Código Producto",
                        value: item.codigoProducto,
                      },

                      {
                        label: "Descripción",
                        value: item.descripcion,
                      },

                      {
                        label: "Fan Size",
                        value: item.fanSize,
                      },
                    ]}
                  />
                ))}
              </div>
            ) : (
              <div className="industrial-empty">
                <div className="industrial-empty-icon">
                  <i className="bi bi-search"></i>
                </div>

                <h3>No encontramos productos</h3>

                <p>
                  No existen coincidencias para la búsqueda o categoría
                  seleccionada.
                </p>

                <button
                  type="button"
                  onClick={() => {
                    setFiltroBusqueda("");
                    setFiltroSeleccionado("todos");
                    setPaginaActual(1);
                  }}
                >
                  <i className="bi bi-arrow-clockwise"></i>
                  Mostrar todos
                </button>
              </div>
            )}

            {/* =================================================
                PAGINACIÓN
            ================================================= */}

            {!isLoading &&
              filtroSeleccionado === "todos" &&
              totalPaginas > 1 && (
                <nav
                  className="industrial-pagination-wrapper"
                  aria-label="Paginación de productos"
                >
                  <button
                    type="button"
                    className="industrial-pagination-arrow"
                    disabled={paginaActual === 1}
                    onClick={() => cambiarPagina(paginaActual - 1)}
                  >
                    <i className="bi bi-chevron-left"></i>

                    <span>Anterior</span>
                  </button>

                  <div className="industrial-pagination-pages">
                    {Array.from(
                      { length: totalPaginas },
                      (_, index) => index + 1,
                    ).map((pagina) => (
                      <button
                        type="button"
                        key={pagina}
                        className={`industrial-page-button ${
                          paginaActual === pagina ? "active" : ""
                        }`}
                        onClick={() => cambiarPagina(pagina)}
                      >
                        {pagina}
                      </button>
                    ))}
                  </div>

                  <button
                    type="button"
                    className="industrial-pagination-arrow"
                    disabled={paginaActual === totalPaginas}
                    onClick={() => cambiarPagina(paginaActual + 1)}
                  >
                    <span>Siguiente</span>

                    <i className="bi bi-chevron-right"></i>
                  </button>
                </nav>
              )}
          </div>
        </section>
      </main>

      <Footer />

      <WhatsAppButton />
    </div>
  );
}

export default IndustrialR;
